import React, { useRef, useState } from "react";
import { Form, Icon, Input } from "semantic-ui-react";
import { Box, Button, CircularProgress, Typography, Alert } from "@mui/material";
import { Link } from "react-router-dom";
import PageTitle from "../../components/PageTitle/PageTitle";
import { useAuth } from "../../context/authContext";
import "./Login.css";
import AuthedUser from "../AuthedUser/AuthedUser";
import { ALERT_MESSAGES, PLACEHOLDER, ROUTES, TITLES, UI_TEXT } from "../../constants";
import { useTranslation } from "react-i18next";
import loginService from "../../services/authService";
import { AuthPageStyles } from "../../utilities/themeColors";
import CompanyLogo from "../../components/CompanyLogo/CompanyLogo";
import useBrandingAssets from "../../hooks/useBrandingAssets";

const Login = () => {
	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [error, setError] = useState(""); // General error message
	const [emailError, setEmailError] = useState(""); // Email-specific error
	const [passwordError, setPasswordError] = useState(""); // Password-specific error
	const [loading, setLoading] = useState(false);
	const loginRequestInFlightRef = useRef(false);
	const { login, isAuthenticated } = useAuth(); // Access the login function from AuthContext
	const [showPassword, setShowPassword] = useState(false);
	const {t} = useTranslation(); 
	const { authBackdropStyle } = useBrandingAssets();

	const handleLogin = async (e) => {
		e.preventDefault();
		if (loginRequestInFlightRef.current) return;
		loginRequestInFlightRef.current = true;
		setError("");
		setEmailError("");
		setPasswordError("");
		setLoading(true);

		let valid = true;

		const emailForLogin = email.trim();

		if (!emailForLogin) {
			setEmailError(t("ALERT_MESSAGES.REQUIRED_EMAIL"));
			valid = false;
		}

		if (!password) {
			setPasswordError(t("ALERT_MESSAGES.REQUIRED_PASSWORD"));
			valid = false;
		}

		if (!valid) {
			loginRequestInFlightRef.current = false;
			setLoading(false);
			return;
		}

			try {
				const reqBody = {
					user_email: emailForLogin,
					user_password: password,
				};

			const response = await loginService(reqBody);
			
			if (response.status) {

				const { token, user, user_meta_data, role } = response?.data || {};
				if (token && user) {
					login(token, user, user_meta_data, role);
				} else {
					setError(t("ALERT_MESSAGES.LOGIN_FAILED"));
				}
			} else {
				const status = response?.status;
				const backendMessage = response?.message || "";

				// Map backend credential failures (your backend uses 400 for these)
				const isCredentialFailure =
					(!status &&
						/invalid password|user not found|invalid credentials/i.test(backendMessage));

				if (isCredentialFailure) {
					setError(t("ALERT_MESSAGES.INVALID_CREDENTIALS"));
				} else if (status === 400 || status === 500) {
					setError(backendMessage || t("ALERT_MESSAGES.ERROR_OCCURED_PLEASE_TRY_AGAIN"));
				} else {
					setError(backendMessage || t("ALERT_MESSAGES.AN_EXPECTED_ERROR"));
				}
			}
		} catch (err) {
			
			// If there's no response, it's likely network/CORS/timeout
			if (!err?.response) {
				setError(
					t("ALERT_MESSAGES.ERROR_OCCURED_PLEASE_TRY_AGAIN") ||
					t('GENERAL.NETWORK_ERROR_PLEASE_CHECK_YOUR_CONNECTION_AND_TRY_AGAIN')
				);
				loginRequestInFlightRef.current = false;
				setLoading(false);
				return;
			}

			const status = err.response?.status;
			const backendMessage = err.response?.data?.message || "";

			// Map backend credential failures (your backend uses 400 for these)
			const isCredentialFailure =
				(status === 400 &&
					/invalid password|user not found|invalid credentials/i.test(backendMessage)) ||
				status === 401 ||
				status === 403;

			if (isCredentialFailure) {
				setError(t("ALERT_MESSAGES.INVALID_CREDENTIALS"));
			} else if (status === 400 || status === 500) {
				setError(backendMessage || t("ALERT_MESSAGES.ERROR_OCCURED_PLEASE_TRY_AGAIN"));
			} else {
				setError(backendMessage || t("ALERT_MESSAGES.AN_EXPECTED_ERROR"));
			}
		}

		loginRequestInFlightRef.current = false;
		setLoading(false);
	};


	const combinedErrorMessage = [emailError, passwordError, error].filter(Boolean).join(" | ");

	if (isAuthenticated) {
		return <AuthedUser />;
	}

	return (
		<Box className="login-container" sx={authBackdropStyle || AuthPageStyles}>
			<Box className="login-form-container">
				<CompanyLogo alt="Logo" className="login-logo" />
				<div className="form-container">
					<PageTitle title={t('BUTTON.LOGIN')} />

					{combinedErrorMessage && (
						<Alert severity="error" sx={{ width: "100%", marginBottom: "0", marginTop: "15px" }}>
							{combinedErrorMessage}
						</Alert>
					)}
					<Form className="login-form" onSubmit={handleLogin} error={!!combinedErrorMessage}>
						<Form.Field error={!!emailError}>
							<Input
								fluid
								placeholder={t('PLACEHOLDER.USERNAME_OR_EMAIL')}
								type="email"
								className="login-input"
								value={email}
								onChange={(e) => setEmail(e.target.value)}
							/>
						</Form.Field>
						<Form.Field error={!!passwordError}>
							<Input
								fluid
								placeholder={t('PLACEHOLDER.PASSWORD')}
								type={showPassword ? 'text' : 'password'}
								className="login-input"
								value={password}
								onChange={(e) => setPassword(e.target.value)}
								icon={
									<Icon
										name={showPassword ? 'eye slash' : 'eye'}
										link
										onClick={() => setShowPassword(!showPassword)}
									/>
								}
							/>
						</Form.Field>
						<Box className="login-links">
							<Link to={ROUTES.FORGOT_PASSWORD} className="login-link">
								{t('UI_TEXT.FORGOT_PASSWORD')}
							</Link>
						</Box>
						<div className="login-button">
							<Button
								variant="contained"
								color="primary"
								className="button-primary"
								type="submit"
								sx={{ minWidth: "120px" }}
								disabled={loading}>
								{loading ? t('BUTTON.LOGGING_IN') : t('BUTTON.LOGIN')}
							</Button>
						</div>
					</Form>
					{loading && (
						<Box display="flex" alignItems="center" justifyContent="center" mt={2}>
							<CircularProgress size={24} sx={{ color: "green" }} />
							<Typography variant="body2" sx={{ marginLeft: 2, color: "green" }}>
								{t('UI_TEXT.VERIFYING')}
							</Typography>
						</Box>
					)}
				</div>
				<Typography variant="body1">
					{t('GENERAL.DONT_HAVE_AN_ACCOUNT')}{" "}
					<Link to={ROUTES.REGISTER} className="login-link">
						{t('BUTTON.REGISTER')}
					</Link>
				</Typography>

			</Box>
		</Box>
	);
};

export default Login;
